'use client';

import { FC } from 'react';
import { PrismicRichText, PrismicLink, PrismicImage } from '@prismicio/react';
import { Content } from '@prismicio/client';
import { ArrowRightIcon } from '@/assets';
import DynamicMarquee from '@/components/DynamicMarquee';

type Props = {
  slice: Content.IntroMediaGridSlice;
};

const HorizontalCarousel: FC<Props> = ({ slice }) => {
  const {
    title,
    description,
    primary_cta: primaryCta,
    media_items: mediaItems,
  } = slice.primary as Content.IntroMediaGridSliceHorizontalCarouselPrimary;

  const items = mediaItems ?? [];
  const half = Math.ceil(items.length / 2);
  const topRow = items.slice(0, half);
  const bottomRow = items.slice(half);

  return (
    <section
      className="relative overflow-hidden bg-white"
      data-slice-type={slice.slice_type}
    >
      {(title || description || primaryCta?.text) && (
        <div className="container relative z-10 mx-auto px-4 py-10 text-[#041118] md:py-16">
          <div className="mx-auto text-left">
            <div className="mb-8">
              {title && (
                <div className="mb-6">
                  <PrismicRichText
                    field={title}
                    components={{
                      heading1: ({ children }) => (
                        <h1 className="font-inter text-4xl font-medium leading-tight md:text-5xl lg:text-6xl">
                          {children}
                        </h1>
                      ),
                    }}
                  />
                </div>
              )}
              {description && (
                <div>
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="font-normal opacity-80">{children}</p>
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {primaryCta?.text && (
              <PrismicLink field={primaryCta} className="secondary-button">
                <ArrowRightIcon className="arrow-icon" />
                {primaryCta.text || 'Learn More'}
              </PrismicLink>
            )}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="relative z-10 flex flex-col gap-4 pb-12 md:gap-6 md:pb-16">
          {topRow.length > 0 && (
            <DynamicMarquee speed={40} gradient={false} direction="left">
              {topRow.map((item, idx) => (
                <div
                  key={`top-${idx}`}
                  className="mx-2 h-[180px] w-[280px] shrink-0 overflow-hidden rounded-[16px] md:mx-3 md:h-[240px] md:w-[380px]"
                >
                  <PrismicImage
                    field={item.media}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </DynamicMarquee>
          )}

          {bottomRow.length > 0 && (
            <DynamicMarquee speed={40} gradient={false} direction="right">
              {bottomRow.map((item, idx) => (
                <div
                  key={`bottom-${idx}`}
                  className="mx-2 h-[180px] w-[280px] shrink-0 overflow-hidden rounded-[16px] md:mx-3 md:h-[240px] md:w-[380px]"
                >
                  <PrismicImage
                    field={item.media}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </DynamicMarquee>
          )}
        </div>
      )}
    </section>
  );
};

export default HorizontalCarousel;
